import axios, { Canceler } from 'axios';
import Session from 'services/Session';

const ISO_8601 = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2,3})Z/;
const DATE_TEST = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2,3})/;
const JSON_TEST = /^\{.*\}$/;

const parseParams = (params: any) => {
    const keys = Object.keys(params);
    let options = '';

    keys.forEach((key) => {
        const isParamTypeObject = typeof params[key] === 'object';
        const isParamTypeArray = isParamTypeObject && params[key] != null && params[key].length >= 0;

        if (!isParamTypeObject) {
            options += `${key}=${params[key]}&`;
        }

        if (isParamTypeObject && isParamTypeArray) {
            params[key].forEach((element: any) => {
                options += `${key}=${element}&`;
            });
        }
    });

    return options ? options.slice(0, -1) : options;
};

type RequestMethod = "get" | "post" | "put" | "delete";

type ResponseType = 'arraybuffer' | 'blob' | 'document' | 'json' | 'text' | 'stream';

type RestUtilResult<T> = {
    promise: Promise<T>;
    cancel: () => void;
};

export class RestUtilWithCancel<T, TPARAM> {

    private axiosToken: Canceler = null;
    private done: boolean = false;

    constructor(
        private method: RequestMethod,
        private url: string,
        private ignoreErrors: boolean = false,
        private response: ResponseType = "json") {
    }

    execute(data: TPARAM): Promise<T> {
        switch (this.method) {
            case "get": return this.get(this.url, data, this.ignoreErrors, this.response);
            case "post": return this.post(this.url, data, this.ignoreErrors, this.response);
            case "put": return this.put(this.url, data, this.ignoreErrors, this.response);
            case "delete": return this.delete(this.url, data);
        }
        return null;
    }

    cancel() {
        if (this.axiosToken && !this.done) {
            this.axiosToken("Canceled");
        }
    }


    private get(url: string, data: FormData | Object | string = {}, ignoreErrors: boolean = false, response: ResponseType = "json"): Promise<T> {
        return this.request('get', url, data, response, ignoreErrors);
    }

    private delete(url: string, data: Object | string, response: ResponseType = "json"): Promise<T> {
        return this.request('delete', url, data, response);
    }

    private put(url: string, data: Object | string, ignoreErrors: boolean = false, response: ResponseType = "json"): Promise<T> {
        return this.request('put', url, data, response, ignoreErrors);
    }

    private post(url: string, data: FormData | Object | string, ignoreErrors: boolean = false, response: ResponseType = "json"): Promise<T> {
        return this.request('post', url, data, response, ignoreErrors);
    }

    private restoreResponse(v: any) {
        Object.keys(v).forEach((k) => {
            try {
                if (v.hasOwnProperty(k)) {
                    if (typeof v[k] == "object" && v[k] !== null) {
                        this.restoreResponse(v[k]);
                    }
                    else {
                        if (ISO_8601.test(v[k]) || DATE_TEST.test(v[k])) {
                            v[k] = new Date(Date.parse(v[k]));
                        }
                        else if (JSON_TEST.test(v[k])) {
                            v[k] = JSON.parse(v[k]);
                        }
                    }
                }
            }
            catch (error) {
                //v[k] = v[k];
            }
        });
    }

    private request(
        method: RequestMethod,
        url: string,
        data: FormData | Object | string = null,
        responseType: ResponseType = "json",
        ignoreErrors: boolean = false
    ): Promise<T> {

        const headers: { [key: string]: string; } = {};

        if (Session.bearerAuthorization != null) headers["Authorization"] = Session.bearerAuthorization;
        headers["Accept"] = "application/json";
        if (ignoreErrors) headers["X-Custom-AxiosIgnoreErrors"] = "1";

        if (data instanceof FormData) {
            headers["Content-Type"] = "multipart/form-data";
        }
        else if (typeof data === 'object') {
            headers["Content-Type"] = "application/json";
        }
        else if (typeof data == 'string') {
            headers["Content-Type"] = "application/x-www-form-urlencoded";
        }

        const serializer = method == "get"
            ? { paramsSerializer: parseParams }
            : {};

        this.cancel();

        this.done = false;
        this.axiosToken = null;

        return new Promise<T>((resolve, reject) => {
            /// Faz a chamada à URL
            axios({
                url: url,
                method: method,
                headers: headers,
                params: method == "get" ? data : null,
                data: method != "get" ? data : null,
                responseType: responseType,
                cancelToken: new axios.CancelToken((c) => {
                    // An executor function receives a cancel function as a parameter
                    this.axiosToken = c;
                }),
                transformResponse: ((data, headers) => {
                    if (data) {
                        // AXIOS 0.22 ou maior não executa o JSON.parse
                        if (typeof data == 'string' && headers["content-type"].indexOf("application/json") > -1) {
                            data = JSON.parse(data);
                        }

                        if (Array.isArray(data))
                            data.forEach((v) => { this.restoreResponse(v); });
                        else
                            this.restoreResponse(data);
                    }
                    return data;
                }),
                ...serializer

            })
                .then((result: any) => {
                    const { status, data } = result;

                    if (status == 200) return data as T;
                    else if (status == 204) return (null);

                    const newError = ({ data, status } as ApiNonContentResult);
                    throw newError;
                })
                .then((value: any) => resolve(value))
                .catch((e: any) => reject(e))
                .finally(() => this.done = true)
                ;
        });
    }
}